'use client';

import React from 'react';

import { Container, Section } from '@layout';
import { withOptionalId } from '@lib/withOptionalId';
import { t as translate } from '@utils/translate';

interface DesignSystemPrinciplesProps {
  sectionID?: string;
  classes?: string;
  isSpanish?: boolean;
}

export interface PrincipleItem {
  title: string;
  body: string;
}

const FALLBACK_ITEMS: PrincipleItem[] = [
  {
    title: 'One primary action per region',
    body: 'Primary actions are used sparingly to maintain clarity and focus.',
  },
  {
    title: 'Semantic colors are reserved for feedback',
    body: 'Warning and error states communicate system status, not hierarchy.',
  },
  {
    title: 'Core patterns precede advanced inputs',
    body: 'Common interactions are prioritized before introducing complexity.',
  },
  {
    title: 'Consistency is driven by tokens',
    body: 'Design tokens ensure alignment across components, themes, and states.',
  },
  {
    title: 'Accessibility is a baseline, not an afterthought',
    body: 'All components are designed to meet contrast, focus, and usability standards.',
  },
];

function parsePrincipleItems(raw: unknown): PrincipleItem[] {
  if (!Array.isArray(raw)) return FALLBACK_ITEMS;
  const out: PrincipleItem[] = [];
  for (const item of raw) {
    if (item && typeof item === 'object' && 'title' in item && 'body' in item) {
      const title = (item as { title: unknown }).title;
      const body = (item as { body: unknown }).body;
      if (typeof title === 'string' && typeof body === 'string' && title.trim() && body.trim()) {
        out.push({ title, body });
      }
    }
  }
  return out.length ? out : FALLBACK_ITEMS;
}

const DesignSystemPrinciples: React.FC<DesignSystemPrinciplesProps> = ({
  sectionID = 'design-system-principles',
  classes = '',
  isSpanish = false,
}) => {
  const locale = isSpanish ? 'es' : 'en';

  const itemsRaw = translate('ui_kit_page.principles.items', 'design-system', locale) as unknown;
  const items = parsePrincipleItems(itemsRaw);

  return (
    <Section
      {...withOptionalId(sectionID)}
      className={`design-system-principles ${classes}`.trim()}
    >
      <Container>
        <div className='panel'>
          <ul className='grid-2 matrix'>
            {items.map((item) => (
              <li key={item.title} className='entry'>
                <p className='name'>{item.title}</p>
                <p className='note'>{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </Section>
  );
};

export default DesignSystemPrinciples;
